import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  FormControl,
  TextField,
  Button,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
// ICONS
import SearchIcon from '@mui/icons-material/Search';
// COMPONENTS
import StudentsJumbotron from './components/StudentsJumbotron';
import StudentListItem from './components/StudentListItem';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
// HOOKS
import { useAppSelector } from '../../../hooks';
// STORE
import {
  adminGetStudents,
  adminLoadMoreStudents,
  getStudentsState,
  IGetStudentList,
} from '../../../store/StudentSlice';

interface IState {
  searchText: string;
  filterData: {
    limit: number;
    offset: number;
  };
}

const defaultState: IState = {
  searchText: '',
  filterData: {
    limit: 30,
    offset: 0,
  },
};

const Students: React.FC = () => {
  // HOOKS
  const dispatch = useDispatch();
  const studentsState = useAppSelector(getStudentsState);

  // LOCAL STATE
  const [state, setState] = useState<IState>(defaultState);

  // USE EFFECT
  useEffect(() => {
    dispatch(adminGetStudents(defaultState.filterData));
  }, []);

  // METHODS
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };
  const handleSearch = () => {
    const { searchText } = state;

    const API_DATA: IGetStudentList = { ...defaultState.filterData };
    if (searchText.trim().length > 0) {
      API_DATA.searchText = searchText;
    }

    setState((prevState) => ({
      ...prevState,
      filterData: defaultState.filterData,
    }));

    dispatch(adminGetStudents(API_DATA));
  };
  const handleLoadMore = () => {
    const { searchText } = state;

    const newoffset = studentsState.list.length;

    const API_DATA: IGetStudentList = {
      ...state.filterData,
      offset: newoffset,
    };
    if (searchText.trim().length > 0) {
      API_DATA.searchText = searchText;
    }

    setState((prevState) => ({
      ...prevState,
      filterData: {
        ...prevState.filterData,
        offset: newoffset,
      },
    }));

    dispatch(adminLoadMoreStudents(API_DATA));
  };

  return (
    <CustomGrayContainer
      style={{ padding: 0, borderRadius: 0, minHeight: 'calc(100vh - 64px)' }}
    >
      <StudentsJumbotron />
      <Box style={{ padding: '0 30px 15px 30px' }}>
        <div style={{ marginBottom: '30px', maxWidth: '400px' }}>
          <FormControl fullWidth>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  size="small"
                  name="searchText"
                  value={state.searchText}
                  placeholder="Search by name"
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                />
              </Grid>

              <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon style={{ marginRight: '-12px' }} />}
                  style={{ minWidth: 'unset' }}
                  onClick={handleSearch}
                />
              </Grid>
            </Grid>
          </FormControl>
        </div>
        <div style={{ marginBottom: '30px' }}>
          {studentsState.list.map((student, index) => (
            <StudentListItem key={index} student={student} />
          ))}
          {studentsState.list.length === 0 &&
            studentsState.status !== 'loading' && (
              <Typography variant="h6">No Students are available</Typography>
            )}
        </div>
        {studentsState.count > studentsState.list.length && (
          <Stack
            direction="row"
            justifyContent="center"
            style={{ marginBottom: '30px' }}
          >
            <Button
              disabled={studentsState.status === 'loading'}
              onClick={handleLoadMore}
            >
              Load More
            </Button>
          </Stack>
        )}
      </Box>
    </CustomGrayContainer>
  );
};

export default Students;
